import { Config } from "../config/Config";
import { ApiOAuthClient } from "./ApiOAuthClient"

interface GetMyselfResponse {
    token: {
        user: {
            id: number;
            userName: string;
            displayName?: string;
        };
        accessToken: string;
        accessTokenExpiresAt: string;
    };
}

export interface PortalWorkspace {
    id: string;
    name: string;
    slug: string | null;
    ownerId: string;
    plan: string;
    createdAt: string;
    updatedAt: string;
}

// GET /v1/workspaces/:id liefert zusaetzlich die Mitglieder (mit eingebettetem user-Objekt) -
// Grundlage fuer den Teilen-Picker in ChatHistory (statt roher User-ID-Eingabe).
export interface PortalWorkspaceMember {
    userId: string;
    role: 'owner' | 'admin' | 'member' | 'viewer' | string;
    user?: {
        id?: number;
        username?: string;
        email?: string;
        displayName?: string;
    };
}

export interface PortalWorkspaceDetail extends PortalWorkspace {
    members?: PortalWorkspaceMember[];
}

export interface PortalConversation {
    id: string;
    workspaceId: string;
    userId: string;
    agentId: string | null;
    type: string;
    title: string | null;
    createdAt: string;
    updatedAt: string;
}

export interface PortalMessageContentPart {
    type: string;
    content: string;
}

export interface PortalMessage {
    id: string;
    role: string;
    content: string;
    contentParts: PortalMessageContentPart[] | null;
    parentId: string | null;
    createdAt: string;
    assets?: unknown[];
}

// Must match SL-CostsService/costs.json's priced types (llm, image_gen, video_gen) plus the
// remaining types PortalBackend's runs API accepts but doesn't price yet (client-supplied cost
// fallback applies to those, see API.md).
export type RunType = 'llm' | 'image_gen' | 'video_gen' | 'stt' | 'ocr' | 'vision' | 'embedding' | 'rag' | 'seo' | 'prompt_opt'
    // 11 image-editing functions - priced per-function in SL-CostsService/costs.json since
    // 2026-07-21 (Task #92 entblockt). PortalBackend's buildCostParams() knows these identifiers.
    // Keep RUN_TYPE_LABELS (BillingPage.tsx) in sync when this list changes.
    | 'image_upscale_2x' | 'image_upscale_4x' | 'image_remove_background' | 'image_remove_object'
    | 'image_extract_object' | 'image_extract_pose' | 'image_expand' | 'image_generate_variations'
    | 'image_convert_to_image' | 'image_spell_correction' | 'image_extract_and_remove';

export interface PortalRun {
    id: string;
    type: string;
    model: string | null;
    status: string;
    inputTokens: number | null;
    outputTokens: number | null;
    cost: number | null;
    startedAt: string | null;
    finishedAt: string | null;
    createdAt: string;
}

export interface CreateRunParams {
    workspaceId: string;
    type: RunType;
    model?: string;
    agentId?: string;
    conversationId?: string;
    imageProjectId?: string;
    inputData?: unknown;
    estimatedCost?: number;
}

export type CreateRunResult =
    | { ok: true; run: PortalRun }
    | { ok: false; reason: 'insufficient_balance' | 'error' };

export type EstimateRunCostResult =
    // estimateKind === 'upper_bound' when the estimate came from the pre-generation mode
    // (inputChars/maxOutputTokens) - the real debit at completeRun is usually lower, so the UI
    // should label it "bis zu X". null/absent for the exact path (inputTokens/outputTokens).
    | { estimated: true; credits: number; estimateKind?: 'upper_bound' | null }
    | { estimated: false; reason: string };

export interface CompleteRunParams {
    outputData?: unknown;
    inputTokens?: number;
    outputTokens?: number;
    cost: number;
    outputAssetId?: string;
    width?: number;
    height?: number;
}

// Payment (Phase 8A/8B) - see PortalBackend's API.md, section "Payment (Wallet, Katalog,
// Checkout, Abo)". All relayed 1:1 from LoginWithSengi/LoginWithSengiPaymentService; shapes
// mirror those repos' API.md exactly rather than inventing a normalized frontend shape.

export interface PortalWalletBalance {
    total: number;
    subscriptionCoins: number;
    prepaidCoins: number;
    postpaidCoins: number;
}

export interface PortalWalletTransaction {
    id: number;
    userId: number;
    clientId: number;
    currency: string;
    delta: number;
    type: 'usage' | 'recharge' | 'subscription_credit' | 'subscription_renewal' | 'refund' | 'bonus' | 'admin_correction';
    reference: string;
    description?: string;
    bucketSubscription: number;
    bucketPrepaid: number;
    bucketPostpaid: number;
    balanceBefore: number;
    balanceAfter: number;
    status: string;
    createdAt: string;
}

// Shapes follow the LIVE PaymentService code (routes/plans.py, routes/checkout.py), NOT its
// API.md - the doc described an older contract (small/medium/large plans with amount_eur/
// interval, prepaid as fixed packages) that the deployed service never returned; the mismatch
// crashed /billing on 2026-07-15. Prepaid is a formula, not a package list: the buyer picks one
// of price_points_eur and the credited coins depend on their current subscription tier
// (credits_per_euro key "none" when no subscription).
export interface PortalSubscriptionPlan {
    id: 'basic' | 'plus' | 'pro';
    name: string;
    // amount_*_eur bleibt Netto (Alt-Feld, unveraendert). net_/gross_/vat_* sind additiv und
    // laenderabhaengig (PaymentService GET /plans?country=..). Optional, weil PortalBackend sie evtl.
    // noch nicht durchreicht - dann faellt die UI auf Netto x (1+VAT_RATE) zurueck (kein Bruch).
    // gross_amount_* ist die Anzeige-Vorschau; autoritativ bleibt Stripe Tax zum Checkout.
    amount_monthly_eur: number;
    amount_yearly_eur: number;
    net_amount_monthly_eur?: number;
    net_amount_yearly_eur?: number;
    gross_amount_monthly_eur?: number;
    gross_amount_yearly_eur?: number;
    vat_rate?: number;
    vat_applicable?: boolean;
    tax_country?: string;
    currency: string;
    coins: number;
    description: string;
}

export interface PortalPrepaidPricePoint {
    net_amount_eur: number;
    gross_amount_eur: number;
}

export interface PortalPrepaidInfo {
    price_points_eur: number[];
    credits_per_euro: Record<string, number>;
    // additiv, optional (siehe PortalSubscriptionPlan) - price_points spiegelt price_points_eur mit
    // Netto/Brutto je Preispunkt; vat_* gilt fuer den ganzen Prepaid-Block.
    price_points?: PortalPrepaidPricePoint[];
    vat_rate?: number;
    vat_applicable?: boolean;
    tax_country?: string;
}

export interface PortalPlansCatalog {
    subscription_plans: PortalSubscriptionPlan[];
    prepaid: PortalPrepaidInfo;
}

export interface PortalSubscription {
    success: boolean;
    subscription_id: string;
    status: string;
    plan?: string;
    plan_id?: string;
    // number = Unix-Epoch-Sekunden (Live-Status von PaymentService), string = ISO-Datum aus dem
    // LoginWithSengi-Fallback (siehe PortalBackend PaymentRoutesV1.ts). formatDate() in
    // BillingPage.tsx handhabt beide Formen.
    current_period_start?: number | string;
    current_period_end?: number | string;
    cancel_at_period_end?: boolean;
    start_time?: string;
    provider: 'stripe' | 'paypal';
    // Stripe-only (PayPal-Checkout ist ohnehin deaktiviert, siehe TASKS.md). Leerstring bei Abos,
    // die vor Einführung dieses Felds angelegt wurden, oder im Stale-Fallback (siehe
    // PaymentRoutesV1.ts - der Fallback-Zweig liefert `interval` gar nicht mit). Laut
    // LoginWithSengiPaymentService/API.md als "unbekannt" behandeln, nicht als Fehler.
    interval?: 'monthly' | 'yearly' | '';
    // true, wenn PaymentServices Live-Status-Call fehlschlug und stattdessen der zuletzt bekannte
    // Stand aus LoginWithSengi angezeigt wird (2026-07-16) — kann z. B. veraltet sein, falls sich
    // der Abo-Status seither bei Stripe/PayPal geändert hat, ohne dass der Webhook durchkam.
    stale?: boolean;
}

export interface CheckoutSessionParams {
    payment_type?: 'subscription' | 'prepaid';
    payment_method?: 'card' | 'google_pay' | 'apple_pay' | 'paypal';
    // subscription only:
    plan?: 'basic' | 'plus' | 'pro';
    interval?: 'monthly' | 'yearly';
    // prepaid only - must be one of the catalog's price_points_eur:
    amount_eur?: number;
    customer_email?: string;
}

export interface CheckoutSessionResult {
    success: boolean;
    session_id?: string;
    order_id?: string;
    subscription_id?: string;
    checkout_url?: string;
    provider?: 'stripe' | 'paypal';
    payment_type?: string;
    plan?: string;
    package?: string;
    credits?: number;
    amount_eur?: number;
    error?: string;
}

export interface PaymentIntentResult {
    success: boolean;
    client_secret?: string;
    payment_intent_id?: string;
    amount_eur?: number;
    currency?: string;
    provider?: string;
    payment_type?: string;
    package?: string;
    credits?: number;
    error?: string;
}

export interface CaptureResult {
    success: boolean;
    order_id?: string;
    status?: string;
    provider?: string;
    payment_type?: string;
    error?: string;
}

export interface CancelSubscriptionResult {
    success: boolean;
    subscription_id?: string;
    status?: string;
    cancel_at?: number;
    canceled_at?: number;
    provider?: string;
    error?: string;
}

export class ApiPortalBackend {

    private static defaultWorkspaceId: string | null = null;

    static GetMyself = async (): Promise<GetMyselfResponse | null> => {
        const response = await ApiOAuthClient.get(Config.json.BackendUrl + "/v1/auth/getmyself");
        if (response == null) return null;
        return (await response.json()) as GetMyselfResponse;
    }

    // Was /v1/chat/getchathistory (dead legacy endpoint, 404) until 2026-07-16 - the real chat
    // history has lived at /v1/conversations all along, see ChatHooks.tsx's persistMessage/
    // loadPersistedMessages, which already used it for individual chats while this list call
    // still pointed at the dead route.
    static GetChats = async (workspaceId: string, type: string = 'chat'): Promise<PortalConversation[] | null> => {
        const response = await ApiOAuthClient.get(Config.json.BackendUrl + `/v1/conversations?workspaceId=${workspaceId}&type=${type}`);
        if (response == null || !response.ok) return null;
        return (await response.json()) as PortalConversation[];
    }

    // Einzelner Workspace inkl. Members (GET /v1/workspaces/:id) - fuer den Teilen-Picker.
    static GetWorkspace = async (id: string): Promise<PortalWorkspaceDetail | null> => {
        const response = await ApiOAuthClient.get(Config.json.BackendUrl + `/v1/workspaces/${id}`);
        if (response == null || !response.ok) return null;
        return (await response.json()) as PortalWorkspaceDetail;
    }

    static GetWorkspaces = async (): Promise<PortalWorkspace[] | null> => {
        const response = await ApiOAuthClient.get(Config.json.BackendUrl + "/v1/workspaces");
        if (response == null || !response.ok) return null;
        return (await response.json()) as PortalWorkspace[];
    }

    // Every logged-in user has at least one workspace (backend ensures this on first login).
    static GetDefaultWorkspaceId = async (): Promise<string | null> => {
        if (ApiPortalBackend.defaultWorkspaceId) return ApiPortalBackend.defaultWorkspaceId;

        const workspaces = await ApiPortalBackend.GetWorkspaces();
        if (!workspaces || workspaces.length === 0) return null;

        ApiPortalBackend.defaultWorkspaceId = workspaces[0].id;
        return ApiPortalBackend.defaultWorkspaceId;
    }

    static CreateConversation = async (workspaceId: string, type: string, title?: string): Promise<PortalConversation | null> => {
        const response = await ApiOAuthClient.post(Config.json.BackendUrl + "/v1/conversations", { workspaceId, type, title });
        if (response == null || !response.ok) return null;
        return (await response.json()) as PortalConversation;
    }

    static UpdateConversation = async (conversationId: string, params: { title?: string; metadata?: unknown }): Promise<PortalConversation | null> => {
        const response = await ApiOAuthClient.put(Config.json.BackendUrl + `/v1/conversations/${conversationId}`, params);
        if (response == null || !response.ok) return null;
        return (await response.json()) as PortalConversation;
    }

    // Response 204 (no body) on success - only the status matters here.
    static DeleteConversation = async (conversationId: string): Promise<boolean> => {
        const response = await ApiOAuthClient.delete(Config.json.BackendUrl + `/v1/conversations/${conversationId}`, null);
        return response != null && response.ok;
    }

    static ShareConversation = async (conversationId: string, userId: string, role: 'viewer' | 'editor' = 'viewer'): Promise<boolean> => {
        const response = await ApiOAuthClient.post(Config.json.BackendUrl + `/v1/conversations/${conversationId}/share`, { userId, role });
        return response != null && response.ok;
    }

    static GetConversationMessages = async (conversationId: string): Promise<PortalMessage[] | null> => {
        const response = await ApiOAuthClient.get(Config.json.BackendUrl + `/v1/conversations/${conversationId}/messages`);
        if (response == null || !response.ok) return null;
        return (await response.json()) as PortalMessage[];
    }

    static PostConversationMessage = async (
        conversationId: string,
        role: string,
        content: string,
        contentParts?: PortalMessageContentPart[]
    ): Promise<PortalMessage | null> => {
        const response = await ApiOAuthClient.post(Config.json.BackendUrl + `/v1/conversations/${conversationId}/messages`, { role, content, contentParts });
        if (response == null || !response.ok) return null;
        return (await response.json()) as PortalMessage;
    }

    // Cost preview before generation (same source SL-CostsService uses server-side for the
    // authoritative cost at PUT /runs/:id/complete). Only "llm"/"image_gen"/"video_gen" are
    // actually priced right now - other RunTypes reply { estimated: false }, see API.md.
    static EstimateRunCost = async (params: { type: RunType; inputTokens?: number; outputTokens?: number; inputChars?: number; maxOutputTokens?: number; width?: number; height?: number; factor?: number; count?: number }): Promise<EstimateRunCostResult | null> => {
        const query = new URLSearchParams();
        query.set('type', params.type);
        if (params.inputTokens !== undefined) query.set('inputTokens', String(params.inputTokens));
        if (params.outputTokens !== undefined) query.set('outputTokens', String(params.outputTokens));
        // Pre-generation "up to" estimate for llm: send raw prompt char count + assumed max output
        // tokens; SL-CostsService does the chars->tokens conversion (so preview and debit share the
        // same tokenizer heuristic). Response then carries estimateKind: "upper_bound". See API.md.
        if (params.inputChars !== undefined) query.set('inputChars', String(params.inputChars));
        if (params.maxOutputTokens !== undefined) query.set('maxOutputTokens', String(params.maxOutputTokens));
        if (params.width !== undefined) query.set('width', String(params.width));
        if (params.height !== undefined) query.set('height', String(params.height));
        // "factor" is only used by image_expand (tiered_factor pricing in SL-CostsService).
        // NOTE (Cross-Repo): PortalBackend's buildCostParams() currently forwards width/height for
        // image_expand instead of factor, so this estimate stays wrong/failing until PortalBackend
        // is fixed - see TASKS.md Task #92. Sent here already so the frontend is forward-compatible.
        if (params.factor !== undefined) query.set('factor', String(params.factor));
        // "count" multipliziert den Pro-Bild/-Variation-Preis server-seitig (count_param in
        // SL-CostsService/costs.json, gesetzt bei image_gen + den 11 image_*-Funktionen; NICHT bei
        // llm/video_gen). Fehlt count -> Faktor 1 (abwaertskompatibel), count <= 0 -> 422, daher nur
        // >= 1 senden. NOTE (Cross-Repo): PortalBackends /runs/estimate muss "count" ebenfalls an
        // SL-CostsService durchreichen, sonst zeigt die Vorschau nur den Einzelpreis - siehe TASKS.md.
        if (params.count !== undefined) query.set('count', String(params.count));

        const response = await ApiOAuthClient.get(Config.json.BackendUrl + `/v1/runs/estimate?${query.toString()}`);
        if (response == null || !response.ok) return null;
        return (await response.json()) as EstimateRunCostResult;
    }

    // Run lifecycle (billing) - POST /runs checks the wallet balance server-side (402 = not
    // enough credits) before a generation is allowed to start. See API.md.
    static CreateRun = async (params: CreateRunParams): Promise<CreateRunResult> => {
        const response = await ApiOAuthClient.post(Config.json.BackendUrl + "/v1/runs", params);
        if (response == null) {
            console.warn('CreateRun: no response (missing access token or network error) for', params);
            return { ok: false, reason: 'error' };
        }
        if (response.status === 402) return { ok: false, reason: 'insufficient_balance' };
        if (!response.ok) {
            console.warn(`CreateRun: unexpected HTTP ${response.status} for`, params);
            return { ok: false, reason: 'error' };
        }
        const run = (await response.json()) as PortalRun;
        // Diagnostic for the "credits are never deducted" investigation (2026-07-15): the actual
        // debit happens server-side in CompleteRun and is entirely invisible from here unless we
        // log it - see the matching log line there.
        console.debug('CreateRun: run started', run.id, run.type);
        return { ok: true, run };
    }

    static CompleteRun = async (runId: string, params: CompleteRunParams): Promise<PortalRun | null> => {
        const response = await ApiOAuthClient.put(Config.json.BackendUrl + `/v1/runs/${runId}/complete`, params);
        if (response == null) {
            console.warn(`CompleteRun: no response (missing access token or network error) for run ${runId}`, params);
            return null;
        }
        if (!response.ok) {
            console.warn(`CompleteRun: unexpected HTTP ${response.status} for run ${runId}`, params);
            return null;
        }
        const run = (await response.json()) as PortalRun;
        // See API.md "PUT /runs/:id/complete": cost is server-recomputed via SL-CostsService when
        // configured/reachable, otherwise it silently stays at whatever `cost` we sent (currently
        // always 0 from this client - see RunCompletionParams). The wallet debit only fires when
        // cost > 0, so a persistently 0 cost here means nothing is ever being deducted - check
        // COSTS_SERVICE_URL/COSTS_SERVICE_API_KEY on the PortalBackend instance if this stays 0.
        console.debug(`CompleteRun: run ${runId} completed with cost=${run.cost}`);
        return run;
    }

    static FailRun = async (runId: string, error: string): Promise<PortalRun | null> => {
        const response = await ApiOAuthClient.put(Config.json.BackendUrl + `/v1/runs/${runId}/fail`, { error });
        if (response == null || !response.ok) {
            console.warn(`FailRun: could not mark run ${runId} as failed (HTTP ${response?.status})`, error);
            return null;
        }
        return (await response.json()) as PortalRun;
    }

    static GetWallet = async (): Promise<{ balance: PortalWalletBalance } | null> => {
        const response = await ApiOAuthClient.get(Config.json.BackendUrl + "/v1/payment/wallet");
        if (response == null || !response.ok) return null;
        const data = await response.json();
        if (typeof data?.balance?.total !== 'number') {
            console.warn('GetWallet: unexpected /v1/payment/wallet response shape:', data);
            return null;
        }
        return data;
    }

    static GetTransactions = async (limit = 50, offset = 0): Promise<{ transactions: PortalWalletTransaction[] } | null> => {
        const response = await ApiOAuthClient.get(Config.json.BackendUrl + `/v1/payment/transactions?limit=${limit}&offset=${offset}`);
        if (response == null || !response.ok) return null;
        const data = await response.json();
        if (!Array.isArray(data?.transactions)) {
            console.warn('GetTransactions: unexpected /v1/payment/transactions response shape:', data);
            return null;
        }
        return data;
    }

    // Ohne Argument loest PortalBackend das Land server-seitig aus dem Token des eingeloggten
    // Nutzers auf (Profil-country aus LoginWithSengi, sonst DE-Fallback) und liefert dann
    // landesrichtige gross_amount_*-Vorschaufelder - das Frontend muss country normalerweise NICHT
    // mitsenden. Der optionale Param ist nur ein expliziter Override (gewinnt vor dem Profil-Land),
    // z. B. fuer einen kuenftigen Laenderwaehler. Kein Land-Raten im Frontend. Siehe TASKS.md.
    static GetPlans = async (country?: string): Promise<PortalPlansCatalog | null> => {
        const query = country ? `?country=${encodeURIComponent(country)}` : '';
        const response = await ApiOAuthClient.get(Config.json.BackendUrl + "/v1/payment/plans" + query);
        if (response == null || !response.ok) return null;
        const data = await response.json();
        // Contract per LoginWithSengiPaymentService/API.md - guard against a deviating live
        // response instead of letting the UI crash on .map() of undefined, and log the actual
        // payload so the mismatch can be reported to the PaymentService/PortalBackend side.
        if (!Array.isArray(data?.subscription_plans)
            || !Array.isArray(data?.prepaid?.price_points_eur)
            || typeof data?.prepaid?.credits_per_euro !== 'object') {
            console.warn('GetPlans: unexpected /v1/payment/plans response shape:', data);
            return null;
        }
        return data as PortalPlansCatalog;
    }

    static GetSubscription = async (): Promise<{ subscription: PortalSubscription | null } | null> => {
        const response = await ApiOAuthClient.get(Config.json.BackendUrl + "/v1/payment/subscription");
        if (response == null || !response.ok) return null;
        const data = await response.json();
        // PortalBackend's shape is inconsistent between the two cases (verified 2026-07-16 by
        // reading PaymentRoutesV1.ts): "no subscription" is wrapped as { subscription: null },
        // but an ACTIVE subscription is returned unwrapped (either PaymentService's relay.data,
        // or the stale-fallback object) - both are the flat PortalSubscription shape directly,
        // never { subscription: {...} }. Trusting only the wrapped shape made every active
        // subscription look like "no subscription" on /billing. Normalize both here.
        if (data && typeof data === 'object' && 'subscription' in data) {
            return { subscription: data.subscription ?? null };
        }
        if (data && typeof data === 'object' && 'status' in data) {
            return { subscription: data as PortalSubscription };
        }
        console.warn('GetSubscription: unexpected /v1/payment/subscription response shape:', data);
        return null;
    }

    static CancelSubscription = async (): Promise<CancelSubscriptionResult | null> => {
        const response = await ApiOAuthClient.post(Config.json.BackendUrl + "/v1/payment/subscription/cancel", {});
        if (response == null) return null;
        return await response.json();
    }

    static CreateCheckoutSession = async (params: CheckoutSessionParams): Promise<CheckoutSessionResult | null> => {
        const response = await ApiOAuthClient.post(Config.json.BackendUrl + "/v1/payment/checkout/session", params);
        if (response == null) return null;
        return await response.json();
    }

    static CreatePaymentIntent = async (params: { amount_eur: number; customer_email?: string }): Promise<PaymentIntentResult | null> => {
        const response = await ApiOAuthClient.post(Config.json.BackendUrl + "/v1/payment/checkout/payment-intent", params);
        if (response == null) return null;
        return await response.json();
    }

    static CapturePayPalOrder = async (orderId: string): Promise<CaptureResult | null> => {
        const response = await ApiOAuthClient.post(Config.json.BackendUrl + "/v1/payment/checkout/capture", { order_id: orderId });
        if (response == null) return null;
        return await response.json();
    }

}
